/* eslint-disable */
// @ts-nocheck

import { useEffect, useMemo, useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import { Combobox } from '@headlessui/react'
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/20/solid'
import { LockClosedIcon } from '@heroicons/react/24/outline'

import { SelectInterface } from '../../interfaces/InterfacesLocal'
import LoadingSpin from '../misc/LoadingSpin'

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

interface InputProps {
  label: string
  disabled?: boolean
  data: SelectInterface[]
  loading?: boolean
  defaultValue?: string | number | null
  changeState?: Function
  className?: string
  showLabel?: boolean
  inputClassName?: string
  placeholder?: string
  optionsClassName?: string
  onChangeValue?: (value: string | number | null) => void
}

export default function ComboBox(props: UseControllerProps & InputProps) {
  const { field, fieldState } = useController(props)
  const {
    label,
    data,
    disabled,
    defaultValue,
    loading,
    className,
    showLabel = true,
    inputClassName,
    placeholder,
    optionsClassName,
    onChangeValue,
  } = props

  const [query, setQuery] = useState<string>('')
  const defaultData = useMemo(() => {
    return data?.find((item) => item.id === defaultValue) ?? null
  }, [data])
  const [selectedPerson, setSelectedPerson] = useState<SelectInterface | null>(
    defaultData
  )
  useEffect(() => {
    if (loading === false) {
      setSelectedPerson(
        data?.find(
          (item) => item.id === defaultValue || item.id === field.value
        ) ?? null
      )
    }
    if (data.length === 0) setSelectedPerson(null)
  }, [data])

  const filteredPeople =
    query === ''
      ? data
      : data.filter((person) => {
          return person.name.toLowerCase().includes(query.toLowerCase())
        })

  return (
    <div className={className ? className : ''}>
      <Combobox
        as="div"
        by={(current, rest) => current?.id === rest?.id}
        disabled={disabled}
        onChange={(e: SelectInterface) => {
          setSelectedPerson(e)
          field.onChange(e?.id!)
          onChangeValue && onChangeValue(e?.id!)
          props.changeState && props.changeState(e)
        }}
        value={selectedPerson}
      >
        {showLabel && (
          <Combobox.Label
            className={`mb-1 block text-sm font-medium ${
              disabled ? 'text-gray-400' : 'text-gray-700'
            } first-letter:uppercase`}
          >
            <span className="inline-flex items-center">
              {label}
              {disabled && <LockClosedIcon className="h-4 px-2" />}
            </span>
          </Combobox.Label>
        )}

        {loading ? (
          <div className="m-1 flex justify-center rounded-md border border-gray-500 py-2">
            <LoadingSpin color="gray-500" />
          </div>
        ) : (
          <div className="relative">
            <Combobox.Input
              className={`${
                fieldState.error
                  ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
                  : `focus:ring-gray-500 ${
                      disabled ? 'border-gray-300' : 'border-gray-400'
                    } text-gray-500 focus:border-gray-500`
              } relative w-full rounded-md border bg-white pl-3 pr-8 text-left shadow-sm sm:text-sm ${inputClassName}`}
              displayValue={(person: SelectInterface) =>
                person?.displayName ?? person?.name
              }
              onChange={(event) => {
                setQuery(event.target.value)
                props.changeState && props.changeState(event.target.value)
              }}
              placeholder={placeholder ?? 'Busque o seleccione'}
            />
            <div className="absolute right-0 top-0 flex items-center gap-1 rounded-r-md p-2 px-2 focus:outline-none">
              {fieldState.error && (
                <ExclamationCircleIcon
                  aria-hidden="true"
                  className="h-5 w-5 text-red-500"
                />
              )}
              <Combobox.Button>
                <ChevronDownIcon
                  aria-hidden="true"
                  className="h-5 w-5 text-gray-400"
                />
              </Combobox.Button>
            </div>

            {filteredPeople.length > 0 && (
              <Combobox.Options
                className={`absolute z-10 mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm ${optionsClassName ? optionsClassName : 'w-full'}`}
              >
                {filteredPeople.map((person, idx) => (
                  <Combobox.Option
                    className={({ active }) =>
                      classNames(
                        'relative cursor-default select-none py-2 pl-3 pr-9',
                        active ? 'bg-slate-600 text-white' : 'text-gray-900'
                      )
                    }
                    key={idx}
                    value={person}
                  >
                    {({ active, selected }) => (
                      <>
                        <span
                          className={classNames(
                            'block truncate',
                            `${selected ? 'font-semibold' : ''}`
                          )}
                        >
                          {person.name}
                        </span>

                        {selected && (
                          <span
                            className={classNames(
                              'absolute inset-y-0 right-0 flex items-center pr-4',
                              active ? 'text-white' : 'text-indigo-600'
                            )}
                          >
                            <CheckIcon aria-hidden="true" className="h-5 w-5" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            )}
            {fieldState.error && (
              <span className="text-xs text-red-600">
                {fieldState.error.message}
              </span>
            )}
          </div>
        )}
      </Combobox>
    </div>
  )
}
